import dayjs from 'dayjs';
import * as ics from 'ics';

import type { IShowing } from 'src/api/landlord-showings-api';

/**
 * Builds an object URL for an ICS calendar invite describing a tour. Mirrors the
 * generator used by the tours table so the calendar view produces identical
 * invites.
 *
 * :param showing: the tour to export
 * :param propertyName: display name of the tour's property
 * :param leadName: display name of the tour's lead
 * :returns: an object URL for the generated ``.ics`` file
 */
export const createTourIcsUrl = async (
    showing: IShowing,
    propertyName: string,
    leadName: string
): Promise<string> => {
    const filename = `Reffie showing with ${leadName}.ics`;
    const startTime = dayjs.utc(showing.start_time);

    const calendarEvent: ics.EventAttributes = {
        startInputType: 'utc',
        startOutputType: 'utc',
        start: [
            startTime.year(),
            startTime.month() + 1,
            startTime.date(),
            startTime.hour(),
            startTime.minute(),
        ],
        duration: { hours: 1, minutes: 0 },
        title: `Showing at ${propertyName} with ${leadName}`,
        description: `Showing at ${propertyName} with ${leadName}`,
        location: propertyName,
    };

    const file: File = await new Promise((resolve, reject) => {
        ics.createEvent(calendarEvent, (error, value) => {
            if (error) {
                reject(error);
            }
            resolve(new File([value], filename, { type: 'text/calendar' }));
        });
    });
    return URL.createObjectURL(file);
};

export const downloadUrl = (url: string, filename: string): void => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
};
